import type { IconCode } from "@components/Atoms/Icon/LucideIcon";
import ButtonStateService from "@core-ui/ContextServices/ButtonStateService/ButtonStateService";
import t from "@ext/localization/locale/translate";
import HeadingMenuButton from "@ext/markdown/elements/heading/edit/components/HeadingMenuButton";
import type { Level } from "@ext/markdown/elements/heading/edit/model/heading";
import EditorService from "@ext/markdown/elementsUtils/ContextServices/EditorService";
import type { Editor } from "@tiptap/core";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
} from "@ui-kit/Dropdown";
import { Icon } from "@ui-kit/Icon";
import { ComponentVariantProvider } from "@ui-kit/Providers";
import { ToolbarDropdownMenuItem, ToolbarToggleGroup, ToolbarTriggerChevron } from "@ui-kit/Toolbar";
import { useCallback } from "react";

const HeadersMenuGroup = ({ editor }: { editor?: Editor }) => {
	const { disabled, isActive } = ButtonStateService.useCurrentAction({
		action: "heading",
	});
	const activeLevel = (isActive ? editor.getAttributes("heading")?.level : 0)?.toString();

	const onSelectHeading = useCallback(
		(level: Level) => {
			if (!editor) return;
			editor.chain().focus().toggleHeading({ level }).run();
			EditorService.setData("lastUsedHeadingLevel", level);
		},
		[editor],
	);

	const onCloseAutoFocus = useCallback(
		(event: Event) => {
			event.preventDefault();
			editor.commands.focus();
		},
		[editor],
	);

	const lastUsedHeadingLevel =
		editor.getAttributes("heading")?.level || EditorService.getData("lastUsedHeadingLevel") || 2;

	return (
		<>
			<ToolbarToggleGroup defaultValue={activeLevel} disabled={disabled} type="single" value={activeLevel}>
				<HeadingMenuButton editor={editor} level={lastUsedHeadingLevel} />
			</ToolbarToggleGroup>
			<ComponentVariantProvider variant="inverse">
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<ToolbarTriggerChevron data-testid="tb-headers" disabled={disabled} sub />
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="lg:shadow-hard-base"
						onCloseAutoFocus={onCloseAutoFocus}
						side="top"
						sideOffset={8}
					>
						<DropdownMenuLabel className="font-normal text-inverse-muted">
							{t("editor.heading")}
						</DropdownMenuLabel>
						{[2, 3, 4].map((level) => (
							<ToolbarDropdownMenuItem
								active={activeLevel === level.toString()}
								key={level}
								onSelect={() => onSelectHeading(level as Level)}
							>
								<Icon icon={`heading-${level}-custom` as IconCode} />
								{t("editor.heading")} {level} <DropdownMenuShortcut value={`Mod-Alt-${level}`} />
							</ToolbarDropdownMenuItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			</ComponentVariantProvider>
		</>
	);
};

export default HeadersMenuGroup;
